/** `flow.rounds` — a versus match played in ROUNDS: a round ends the moment a
 * seat loses a life (`core.vitals`' `hurt` signal) or its clock runs out. The
 * seat that dealt every blow of that tick — and was not hit itself — wins the
 * round and scores; a trade of blows on one tick, or the clock, is a tied
 * round that scores nobody and is replayed. The arena then freezes for a
 * break (every seat and every `sweepTag` entity, the shells in flight), and
 * the next round starts with the shells swept and every seat back at its
 * spawn point (vitals' respawn point) with its lives refilled (a `restore`
 * signal), each sent a `round-start` signal its body and weapon reset on.
 * The rounds won are the seats' scores, so
 * `flow.objective`'s `first-to` decides the match; an optional match clock
 * ends an undecided match as a draw. */
import { id, int, obj } from '../schemaBuilders.js';
import { defineModule, num as read, str, type ModuleCtx } from '../module.js';
import type { EntityV1 } from '../world.js';
import { shooterSlot } from './misc.js';

export type RoundsConfig = {
  /** A round's clock (ticks); 0 = untimed. */
  roundTicks: number;
  /** The frozen break between rounds (ticks); it also lasts until every
   * downed seat has finished its hurt pose. */
  breakTicks: number;
  /** Points a round win scores. */
  points: number;
  /** Entities frozen with the round and swept before the next (shells). */
  sweepTag: string;
  /** The whole match's clock (ticks): reaching it undecided is a draw; 0 = none. */
  matchTicks: number;
};

type RoundsCtx = ModuleCtx<RoundsConfig>;

const seats = (ctx: RoundsCtx): EntityV1[] => ctx.entities({ tag: 'player', status: 'any' }).filter(e => e.slot !== null);

function freeze(ctx: RoundsCtx): void {
  for (const e of [...seats(ctx), ...ctx.entities({ tag: ctx.config.sweepTag })]) if (e.status === 'active') e.status = 'frozen';
}

/** Decide a round that just ended: the seats credited with every blow, less
 * the seats hit. One seat left wins it. */
function decide(ctx: RoundsCtx, blows: ReturnType<RoundsCtx['signals']>): void {
  const s = ctx.state;
  const hit = new Set(blows.map(b => (typeof b.data.slot === 'number' ? b.data.slot : -1)));
  const by = new Set(blows.map(b => shooterSlot(ctx, typeof b.data.by === 'string' ? b.data.by : null)));
  const winners = [...by].filter((slot): slot is number => slot !== null && !hit.has(slot));
  const at = blows[0]!.data;
  if (by.size === 1 && winners.length === 1) {
    ctx.addScore(winners[0]!, ctx.config.points);
    ctx.emit('round-won', { x: typeof at.x === 'number' ? at.x : 0, y: typeof at.y === 'number' ? at.y : 0, slot: winners[0]!, round: read(s, 'round') });
  } else ctx.emit('round-tied', { round: read(s, 'round'), reason: 'trade' });
}

function startBreak(ctx: RoundsCtx): void {
  Object.assign(ctx.state, { status: 'break', left: ctx.config.breakTicks });
  freeze(ctx);
}

function startRound(ctx: RoundsCtx): void {
  const s = ctx.state;
  for (const e of ctx.entities({ tag: ctx.config.sweepTag, status: 'any' })) ctx.despawn(e, 'swept');
  for (const e of seats(ctx)) {
    const v = ctx.peer(e, 'core.vitals');
    if (!v || str(v, 'phase') !== 'alive' || e.tags.includes('out')) continue;
    e.x = read(v, 'spawnX'); e.y = read(v, 'spawnY'); e.vx = 0; e.vy = 0;
    e.facing = read(v, 'facing') === -1 ? -1 : 1;
    e.status = 'active';
    ctx.signal('restore', e, null, {});
    ctx.signal('round-start', e, null, { round: read(s, 'round') + 1 });
  }
  Object.assign(s, { status: 'play', left: ctx.config.roundTicks, round: read(s, 'round') + 1 });
  ctx.setScalar('round.number', read(s, 'round'));
  ctx.emit('round-start', { round: read(s, 'round') });
}

export const rounds = defineModule<RoundsConfig>({
  id: 'flow.rounds', version: 1, attach: 'global', role: 'flow',
  summary: 'Versus rounds: a lost life or the round clock ends the round; the seat that dealt every blow and took none scores it, a trade or the clock ties it; a frozen break, then every seat back at its spawn with the shells swept. Optional match clock ends an undecided match as a draw.',
  config: obj({ roundTicks: int(0, 36000), breakTicks: int(1, 600), points: int(1, 100), sweepTag: id(), matchTicks: int(0, 108000) }),
  requires: ['core.vitals', 'flow.objective'], events: ['round-won', 'round-tied', 'round-start'],
  presentation: {
    'round-won': { cue: 'score', vfx: { effect: 'popup', text: 'ROUND!', palette: 'slot' } },
    'round-tied': { cue: 'timeup' },
    'round-start': { cue: 'countdown' },
  },
  proofs: ['a-lost-life-ends-the-round', 'the-shooter-scores-it', 'a-trade-ties-it', 'the-clock-ties-it', 'frozen-break', 'break-waits-for-the-hurt-pose',
    'next-round-back-at-spawn-and-swept', 'match-clock-draws'],
  animStates: () => [],
  hud: () => [{ id: 'round', label: 'ROUND', kind: 'counter' }, { id: 'clock', label: 'TIME', kind: 'timer' }, { id: 'wins', label: 'WINS', kind: 'text' }],
  hudValues(ctx) {
    const s = ctx.state;
    return { round: read(s, 'round'), clock: s.status === 'play' && ctx.config.roundTicks > 0 ? Math.max(0, Math.ceil(read(s, 'left') / 30)) : 0,
      wins: ctx.world.scores.join(' - ') };
  },
  initGlobal(ctx) {
    Object.assign(ctx.state, { status: 'play', left: ctx.config.roundTicks, round: 1 });
    ctx.setScalar('round.number', 1);
  },
  systems: {
    // Rules: a finished break starts the next round BEFORE vitals runs, so
    // vitals refills the lives on the same tick.
    rules(ctx) {
      if (ctx.world.phase !== 'active' || ctx.state.status !== 'break') return;
      const s = ctx.state;
      s.left = Math.max(0, read(s, 'left') - 1);
      // The break lasts until it has run and every downed seat has finished its hurt pose.
      const posing = seats(ctx).some(e => !e.tags.includes('out') && str(ctx.peer(e, 'core.vitals') ?? {}, 'phase') === 'hurt');
      if (read(s, 'left') === 0 && !posing) startRound(ctx);
    },
    // Flow: after vitals took this tick's blows, before the objective reads the scores.
    flow(ctx) {
      if (ctx.world.phase !== 'active') return;
      const c = ctx.config, s = ctx.state;
      if (c.matchTicks > 0 && ctx.world.tick >= c.matchTicks) {
        ctx.emit('round-tied', { round: read(s, 'round'), reason: 'match-clock' });
        ctx.end('won', 'draw', null);
        return;
      }
      // The break: everything stays frozen (a seat vitals respawned included).
      if (s.status === 'break') { freeze(ctx); return; }
      const blows = ctx.signals('hurt').filter(b => ctx.entity(b.target)?.slot !== null && ctx.entity(b.target)?.tags.includes('player'));
      if (blows.length) { decide(ctx, blows); startBreak(ctx); return; }
      if (c.roundTicks > 0) {
        s.left = read(s, 'left') - 1;
        if (read(s, 'left') <= 0) { ctx.emit('round-tied', { round: read(s, 'round'), reason: 'clock' }); startBreak(ctx); }
      }
    },
  },
});
